import { useState } from 'react'
import { trpc, type RouterOutput } from '@/config/trpc'
import { Button } from '@/components/ui/shadcn/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/shadcn/dialog'
import { Checkbox } from '@/components/ui/shadcn/checkbox'
import { RefreshCw } from 'lucide-react'
import {
  FacebookIcon,
  InstagramIcon,
  LinkedinIcon,
  TikTokIcon,
  YoutubeIcon,
} from '@/components/ui/icons/icons-new'
import { cn } from '@/lib/utils'

const platformConfig = [
  { id: 'instagram' as const, label: 'Instagram', Icon: InstagramIcon },
  { id: 'linkedin' as const, label: 'LinkedIn', Icon: LinkedinIcon },
  { id: 'tiktok' as const, label: 'TikTok', Icon: TikTokIcon },
  { id: 'youtube' as const, label: 'YouTube', Icon: YoutubeIcon },
  { id: 'facebook' as const, label: 'Facebook', Icon: FacebookIcon },
]

type PlatformId = (typeof platformConfig)[number]['id']

export function TestSyncDialog() {
  const [open, setOpen] = useState(false)
  const [selectedPlatform, setSelectedPlatform] = useState<PlatformId | undefined>()
  const [selectedPostIds, setSelectedPostIds] = useState<number[]>([])
  const [includeAccountMetrics, setIncludeAccountMetrics] = useState(false)
  const [result, setResult] = useState<{
    synced: number
    failed: number
    total: number
    errors: string[]
  } | null>(null)

  const utils = trpc.useUtils()

  // Fetch posts to show as selectable list
  const { data: postsData } = trpc.analytics.overview.useQuery(
    {
      platforms: selectedPlatform ? [selectedPlatform] : undefined,
      limit: 100,
      offset: 0,
    },
    { enabled: open },
  )

  const syncMutation = trpc.analytics.triggerSync.useMutation({
    onSuccess: (data) => {
      setResult(data)
      utils.analytics.overview.invalidate()
      utils.analytics.accountOverview.invalidate()
    },
  })

  const backfillMutation = trpc.analytics.backfillYoutubeCaptions.useMutation({
    onSuccess: () => {
      utils.analytics.captionLength.invalidate()
      utils.analytics.hashtagEffect.invalidate()
      utils.analytics.hookLength.invalidate()
      utils.analytics.captionCraft.invalidate()
    },
  })

  const classifyMutation = trpc.analytics.backfillContentCategories.useMutation({
    onSuccess: () => {
      utils.analytics.contentTypeImpact.invalidate()
      utils.analytics.contentMix.invalidate()
    },
  })

  const handleSync = () => {
    setResult(null)
    syncMutation.mutate({
      platform: selectedPlatform,
      trackedPostIds: selectedPostIds.length > 0 ? selectedPostIds : undefined,
      includeAccountMetrics,
    })
  }

  const togglePost = (id: number) => {
    setSelectedPostIds((prev) => (prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]))
  }

  const selectAll = () => {
    if (!postsData) return
    const allIds = postsData.posts.map((p) => p.trackedPost.id)
    setSelectedPostIds(allIds)
  }

  const selectNone = () => setSelectedPostIds([])

  const posts = postsData?.posts ?? []
  const syncTarget =
    selectedPostIds.length > 0
      ? `${selectedPostIds.length} post${selectedPostIds.length > 1 ? 's' : ''}`
      : selectedPlatform
        ? `all ${selectedPlatform} posts`
        : 'all posts'

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        setOpen(v)
        if (!v) {
          setResult(null)
          setSelectedPostIds([])
        }
      }}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <RefreshCw size={13} />
          Test sync
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[80vh] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Test Analytics Sync</DialogTitle>
        </DialogHeader>

        <div className="flex min-w-0 flex-col gap-4 overflow-hidden">
          {/* Platform filter */}
          <div>
            <p className="text-muted-foreground mb-2 text-xs font-medium uppercase tracking-wider">
              Platform
            </p>
            <div className="flex flex-wrap gap-1.5">
              <button
                onClick={() => {
                  setSelectedPlatform(undefined)
                  setSelectedPostIds([])
                }}
                className={cn(
                  'rounded-full px-3 py-1 text-xs font-medium transition-all',
                  !selectedPlatform
                    ? 'bg-primary text-primary-foreground'
                    : 'border-border bg-background text-foreground hover:bg-hover border',
                )}>
                All
              </button>
              {platformConfig.map(({ id, label, Icon }) => (
                <button
                  key={id}
                  onClick={() => {
                    setSelectedPlatform(selectedPlatform === id ? undefined : id)
                    setSelectedPostIds([])
                  }}
                  className={cn(
                    'flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium transition-all',
                    selectedPlatform === id
                      ? 'bg-primary text-primary-foreground'
                      : 'border-border bg-background text-foreground hover:bg-hover border',
                  )}>
                  <Icon width={12} height={12} color="currentColor" />
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Post selection */}
          <div>
            <div className="mb-2 flex items-center justify-between">
              <p className="text-muted-foreground text-xs font-medium uppercase tracking-wider">
                Posts {posts.length > 0 && `(${posts.length})`}
              </p>
              {posts.length > 0 && (
                <div className="flex gap-2">
                  <button onClick={selectAll} className="text-primary text-xs hover:underline">
                    Select all
                  </button>
                  <button
                    onClick={selectNone}
                    className="text-muted-foreground text-xs hover:underline">
                    None
                  </button>
                </div>
              )}
            </div>
            <div className="border-border max-h-48 overflow-y-auto overflow-x-hidden rounded-md border">
              {posts.length === 0 ? (
                <p className="text-muted-foreground p-3 text-center text-xs">No posts to sync</p>
              ) : (
                posts.map(({ trackedPost }) => (
                  <label
                    key={trackedPost.id}
                    className="hover:bg-hover flex min-w-0 cursor-pointer items-center gap-3 px-3 py-2 transition-colors">
                    <Checkbox
                      checked={selectedPostIds.includes(trackedPost.id)}
                      onCheckedChange={() => togglePost(trackedPost.id)}
                    />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-xs">{trackedPost.caption || 'No caption'}</p>
                      <p className="text-muted-foreground/60 text-[10px]">
                        {trackedPost.platform} &middot;{' '}
                        {new Date(trackedPost.publishedAt).toLocaleDateString()}
                      </p>
                    </div>
                  </label>
                ))
              )}
            </div>
            <p className="text-muted-foreground/60 mt-1 text-[10px]">
              Leave empty to sync {selectedPlatform ? `all ${selectedPlatform}` : 'all'} posts
            </p>
          </div>

          {/* Options */}
          <label className="flex cursor-pointer items-center gap-2">
            <Checkbox
              checked={includeAccountMetrics}
              onCheckedChange={(v) => setIncludeAccountMetrics(v === true)}
            />
            <span className="text-sm">Include account metrics (followers, etc.)</span>
          </label>

          {/* Result */}
          {result && (
            <div className="bg-muted flex flex-col gap-1 rounded-md p-3 text-sm">
              <div>
                Synced <span className="font-medium">{result.synced}</span> of {result.total} posts
                {result.failed > 0 && (
                  <span className="text-destructive"> ({result.failed} failed)</span>
                )}
              </div>
              {result.errors.map((err: string, i: number) => (
                <p key={i} className="text-destructive/80 text-xs">
                  {err}
                </p>
              ))}
              {result.errors.some((e: string) => e.includes('403')) && (
                <p className="text-muted-foreground mt-1 text-xs">
                  A 403 error usually means the platform needs to be re-connected with updated
                  permissions.{' '}
                  <a href="/account/social-media" className="text-primary hover:underline">
                    Reconnect in settings
                  </a>
                </p>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-col gap-2">
            <Button onClick={handleSync} disabled={syncMutation.isPending}>
              {syncMutation.isPending ? (
                <span className="flex items-center gap-2">
                  <RefreshCw size={13} className="animate-spin" />
                  Syncing {syncTarget}...
                </span>
              ) : (
                `Sync ${syncTarget}`
              )}
            </Button>

            {/* One-shot backfill: rewrites caption from title→description for
                all existing YouTube tracked posts in the brand. Safe to call
                repeatedly. */}
            <Button
              variant="outline"
              onClick={() => backfillMutation.mutate({})}
              disabled={backfillMutation.isPending}>
              {backfillMutation.isPending ? (
                <span className="flex items-center gap-2">
                  <RefreshCw size={13} className="animate-spin" />
                  Backfilling YouTube captions...
                </span>
              ) : backfillMutation.data ? (
                `Backfilled ${backfillMutation.data.totalUpdated} YT captions`
              ) : (
                'Backfill YouTube captions'
              )}
            </Button>

            {/* Classify all unclassified posts in the brand. Idempotent —
                skips posts that already have a category. */}
            <Button
              variant="outline"
              onClick={() => classifyMutation.mutate({ force: false })}
              disabled={classifyMutation.isPending}>
              {classifyMutation.isPending ? (
                <span className="flex items-center gap-2">
                  <RefreshCw size={13} className="animate-spin" />
                  Classifying content...
                </span>
              ) : classifyMutation.data ? (
                `Classified ${classifyMutation.data.classified} of ${classifyMutation.data.total}`
              ) : (
                'Classify content categories'
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
